<template>
  <transition name="modal-fade">
    <div v-if="workspace" class="transform-modal-overlay" @click.self="close">
      <div class="transform-modal" @click.stop>
        <div class="transform-modal-form">
          <div class="transform-modal-header">
            <div class="transform-modal-icon icon-wand">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
              </svg>
            </div>
            <div>
              <h3 class="transform-modal-title">Réorganiser avec l'IA</h3>
              <p class="transform-modal-subtitle">« {{ workspace.name }} »</p>
            </div>
            <button class="transform-modal-close" @click="close">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
              </svg>
            </button>
          </div>
          <p class="transform-instructions-label">
            Des consignes particulières&nbsp;? (optionnel — par défaut, l'agent choisit l'organisation la plus adaptée au contenu)
          </p>
          <div class="transform-search-bar" :class="{ 'transform-search-bar-multiline': hasMultipleLines }">
            <textarea
              v-model="instructions"
              class="transform-search-input"
              placeholder="Comment veux-tu organiser ce dossier ?"
              rows="1"
              ref="instructionsBox"
              :disabled="isLoading"
              @input="adjustTextareaHeight"
              @keydown="onKeydown"
            ></textarea>
            <button
              type="button"
              class="transform-send-circle"
              :disabled="isLoading"
              @click.stop="submit"
              title="Réorganiser"
            >
              <svg v-if="isLoading" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="spinner-icon" viewBox="0 0 16 16">
                <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
              </svg>
              <svg v-else class="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          </div>
          <p v-if="error" class="reorg-modal-error">{{ error }}</p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { getSupabaseToken } from '@/services/api';

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

export default {
  name: 'WorkspaceReorganizeModal',

  props: {
    // Le workspace à réorganiser ; null = modal fermé.
    workspace: { type: Object, default: null },
  },

  data() {
    return {
      instructions: '',
      isLoading: false,
      error: null,
      // Vrai dès que le contenu occupe plus d'une ligne (wrap OU \n) —
      // posé par adjustTextareaHeight, seule source fiable pour le wrap.
      isMultiline: false,
    };
  },

  computed: {
    hasMultipleLines() {
      return this.isMultiline;
    },
  },

  watch: {
    workspace(val) {
      if (val) {
        this.instructions = '';
        this.isMultiline = false;
        this.error = null;
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          if (this.$refs.instructionsBox) {
            this.$refs.instructionsBox.focus();
            this.adjustTextareaHeight();
          }
        });
      } else {
        document.body.style.overflow = '';
      }
    },
  },

  methods: {
    close() {
      if (this.isLoading) return;
      this.$emit('close');
    },

    onKeydown(e) {
      if (e.key === 'Enter') {
        if (e.shiftKey) return; // Shift+Enter = nouvelle ligne
        e.preventDefault();
        this.submit();
      }
      if (e.key === 'Escape') this.close();
    },

    adjustTextareaHeight() {
      const el = this.$refs.instructionsBox;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
        // Bascule pilule → rectangle dès que le contenu occupe plus d'une
        // ligne — wrap visuel compris, pas seulement sur Shift+Entrée.
        // Une ligne en mode pilule : 24px de texte + 32px de padding = 56 ;
        // en mode rectangle : 24 + 24 = 48. Seuil à 60 = stable dans les
        // deux sens, sans flicker.
        this.isMultiline = this.instructions.includes('\n') || el.scrollHeight > 60;
        void el.offsetHeight;
      }
    },

    async submit() {
      if (this.isLoading || !this.workspace) return;
      this.isLoading = true;
      this.error = null;
      try {
        const token = await getSupabaseToken();
        const resp = await axios.post(
          `${API_URL}/copilot/api/v1/workspace/${this.workspace.id}/reorganize`,
          { instructions: this.instructions.trim(), notify: true },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        this.$emit('created', {
          id: resp.data.workspace_id,
          name: resp.data.name,
          status: resp.data.status || 'queued',
        });
      } catch (e) {
        console.error('reorganize trigger failed', e);
        const status = e.response && e.response.status;
        if (status === 409) {
          this.error = 'Le workspace doit être indexé (statut « Prêt ») pour être réorganisé.';
        } else {
          this.error = (e.response && e.response.data && e.response.data.error) || 'Le lancement de la réorganisation a échoué.';
        }
      } finally {
        this.isLoading = false;
      }
    },
  },

  beforeDestroy() {
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* Même langage que TransformModal (mémo/e-mail) — overlay, carte, pill. */
.transform-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.transform-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 640px;
  max-width: 92%;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.transform-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.transform-modal-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-wand {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #625CFB;
}

.transform-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
}

.transform-modal-subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.transform-modal-close {
  margin-left: auto;
  background: none;
  border: none;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.transform-modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.transform-instructions-label {
  margin: 16px 24px 8px;
  font-size: 13px;
  color: #6b7280;
}

.transform-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  min-height: 56px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 9999px;
  margin: 0 24px 24px;
  box-sizing: border-box;
}

.transform-search-bar-multiline {
  flex-direction: column;
  align-items: stretch;
  padding: 12px;
  min-height: auto;
  border-radius: 16px;
}

.transform-search-bar-multiline .transform-search-input {
  min-height: 48px;
  padding: 12px 0;
  flex: 0 0 auto;
}

.transform-search-bar-multiline .transform-send-circle {
  align-self: flex-end;
}

.transform-search-input {
  flex: 1;
  min-width: 0;
  min-height: 24px;
  max-height: 400px;
  padding: 16px 8px;
  margin: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  line-height: 24px;
  resize: none;
  overflow-y: auto;
  color: #000;
}

.transform-search-input::placeholder {
  color: #9e9e9e;
}

.transform-send-circle {
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  background: #625CFB;
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(98, 92, 251, 0.35);
}

.transform-send-circle:hover:not(:disabled) {
  background: #5147e5;
  box-shadow: 0 4px 12px rgba(98, 92, 251, 0.45);
  transform: translateY(-1px);
}

.transform-send-circle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.reorg-modal-error {
  margin: -12px 24px 20px;
  font-size: 13px;
  color: #b91c1c;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-active .transform-modal,
.modal-fade-leave-active .transform-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .transform-modal,
.modal-fade-leave-to .transform-modal {
  transform: translateY(16px) scale(0.97);
  opacity: 0;
}

@media (max-width: 640px) {
  .transform-modal-overlay {
    padding: 12px;
    align-items: flex-end;
  }
  .transform-modal {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
  }
}
</style>
